import {
  AccountTree,
  ArrowDropDown,
  CheckCircle,
  Error,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Popper,
} from '@mui/material';
import { isArray } from 'lodash';
import { ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAllLegalEntities } from '~/data/legalEntity';
import { useAllOrgUnits } from '~/data/orgunits';
import {
  AnalyticsReportParams,
  AnalyticsReportRoleScope,
  AnalyticsReportScope,
  AnalyticsReportSettingsProps,
  AnalyticsReportTypeLegalEntity,
  AnalyticsReportTypeUnit,
  AnalyticsReportTypes,
} from '~/data/reports';
import { AnalyticsReportConfig } from '~/scenes/PortalModules/Analytics/common/ReportDatasetSelector/data';
import {
  ReportTypeSelector,
  useReportTypeLabels,
} from '~/scenes/PortalModules/Analytics/common/ReportDatasetSelector/ReportTypeSelector';
import { UseReportDatasetOutput } from '~/scenes/PortalModules/Analytics/common/ReportDatasetSelector/useReportDataset';
import { useSxStyles } from '~/scenes/PortalModules/Analytics/common/ReportDatasetSelector/useStyles';

type PropsType<P> = UseReportDatasetOutput<P> & {
  renderParamsFields?: (props: {
    params: AnalyticsReportParams<P>;
    onChange: (params: AnalyticsReportParams<P>) => void;
    reportConfig: AnalyticsReportConfig;
  }) => ReactNode;
  renderCurrentParams?: (props: {
    params?: AnalyticsReportParams<P>;
  }) => ReactNode;
  isLoading?: boolean;
  failedToLoad?: boolean;
};

export const ReportDatasetSelector = <P extends object>(
  props: PropsType<P>,
) => {
  const {
    reportEnabled,
    reportSelection: currentReportSelection,
    reportConfig,
    setReportSelection,
    renderParamsFields,
    renderCurrentParams,
    isLoading,
    failedToLoad,
  } = props;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleToggleSelectors = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  const handleCloseSelectors = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const [selectorSelection, setSelectorSelection] = useState<
    AnalyticsReportSettingsProps<P>
  >(currentReportSelection);

  const applyReportSettings = useCallback(() => {
    setReportSelection(selectorSelection);
    handleCloseSelectors();
  }, [setReportSelection, selectorSelection, handleCloseSelectors]);

  const resetReportSettings = useCallback(() => {
    setSelectorSelection(currentReportSelection);
    handleCloseSelectors();
  }, [setSelectorSelection, currentReportSelection, handleCloseSelectors]);

  const isReportType = useCallback(
    (types: AnalyticsReportTypes | AnalyticsReportTypes[]): boolean => {
      const selectedType = selectorSelection.reportType.type;

      if (isArray(types)) {
        return types.some((value) => value === selectedType);
      }
      return types === selectedType;
    },
    [selectorSelection],
  );

  const sxStyles = useSxStyles();

  const { orgUnitsIncludeDeleted } = useAllOrgUnits();
  const { legalEntities } = useAllLegalEntities();

  const handleReportTypeChange = useCallback(
    (newTab: AnalyticsReportTypes) => {
      setSelectorSelection((previous) => ({
        ...previous,
        reportType: { ...previous.reportType, type: newTab },
      }));
    },
    [setSelectorSelection],
  );

  const handleRoleSelection = useCallback(
    (role?: AnalyticsReportRoleScope) => {
      if (isReportType(AnalyticsReportTypes.Team) && !!role) {
        setSelectorSelection((previous) => ({
          ...previous,
          reportType: {
            ...previous.reportType,
            role,
          },
        }));
      }
    },
    [isReportType, setSelectorSelection],
  );

  const handleUnitSelection = useCallback(
    (unitId?: number) => {
      if (isReportType(AnalyticsReportTypes.Unit) && !!unitId) {
        setSelectorSelection((previous) => ({
          ...previous,
          reportType: {
            ...previous.reportType,
            unitId,
          },
        }));
      }
    },
    [isReportType, setSelectorSelection],
  );

  const handleLegalEntitySelection = useCallback(
    (legalEntityId?: number) => {
      if (isReportType(AnalyticsReportTypes.LegalEntity) && !!legalEntityId) {
        setSelectorSelection((previous) => ({
          ...previous,
          reportType: {
            ...previous.reportType,
            legalEntityId,
          },
        }));
      }
    },
    [isReportType, setSelectorSelection],
  );

  const handleIncludeSubunits = useCallback(
    (checked: boolean) => {
      if (
        isReportType([AnalyticsReportTypes.Unit, AnalyticsReportTypes.Team])
      ) {
        setSelectorSelection((previous) => ({
          ...previous,
          reportType: {
            ...previous.reportType,
            scope: checked
              ? AnalyticsReportScope.All
              : AnalyticsReportScope.Direct,
          },
        }));
      }
    },
    [isReportType, setSelectorSelection],
  );

  const handleParamsChange = useCallback(
    (newParams: AnalyticsReportParams<P>) => {
      setSelectorSelection((previous) => ({
        ...previous,
        params: newParams,
      }));
    },
    [setSelectorSelection],
  );

  const { t } = useTranslation();
  const { getTypeLabel, getRoleLabel } = useReportTypeLabels();

  const resolveCurrentSettings = useCallback(() => {
    const currentType = currentReportSelection.reportType.type;
    const currentRole = currentReportSelection.reportType.role;
    const currentScope = currentReportSelection.reportType.scope;
    const currentParams = currentReportSelection.params;

    const scopeIcon =
      currentScope === AnalyticsReportScope.All ? (
        <AccountTree fontSize={'inherit'} sx={sxStyles.selectedIcon} />
      ) : (
        <></>
      );

    const resolveTypeContent = () => {
      switch (currentType) {
        case AnalyticsReportTypes.Company:
          return <>{`${getTypeLabel(currentType)}`}</>;
        case AnalyticsReportTypes.Team:
          return (
            <>
              {`${getTypeLabel(currentType)}: ${getRoleLabel(currentRole)}`}{' '}
              {scopeIcon}
            </>
          );
        case AnalyticsReportTypes.HR:
          return (
            <>
              {`${getTypeLabel(currentType)}`} {scopeIcon}
            </>
          );
        case AnalyticsReportTypes.Employee:
          return (
            <>
              {`${getTypeLabel(currentType)}`} {scopeIcon}
            </>
          );
        case AnalyticsReportTypes.Unit: {
          const { unitId } =
            currentReportSelection.reportType as AnalyticsReportTypeUnit;

          const foundUnit = orgUnitsIncludeDeleted.find(
            (unit) => unit.fOrgUnitId === unitId,
          );

          return (
            <>
              {`${getTypeLabel(currentType)}: ${
                foundUnit?.fOrgUnitName ?? t('laUnknown')
              }`}{' '}
              {scopeIcon}
            </>
          );
        }
        case AnalyticsReportTypes.LegalEntity: {
          const { legalEntityId } =
            currentReportSelection.reportType as AnalyticsReportTypeLegalEntity;

          const foundLegalEntity = legalEntities.find(
            (le) => le.fLegalEntityId === legalEntityId,
          );

          return (
            <>
              {`${getTypeLabel(currentType)}: ${
                foundLegalEntity?.fLegalEntityName ?? t('laUnknown')
              }`}{' '}
            </>
          );
        }
        default:
          return <></>;
      }
    };

    return (
      <Box>
        <Box sx={sxStyles.currentSelectionType}>{resolveTypeContent()}</Box>
        {!!renderCurrentParams && (
          <Box sx={sxStyles.currentSelectionLine}>
            {renderCurrentParams({ params: currentParams })}
          </Box>
        )}
      </Box>
    );
  }, [
    currentReportSelection,
    getTypeLabel,
    getRoleLabel,
    renderCurrentParams,
    t,
    orgUnitsIncludeDeleted,
    legalEntities,
    sxStyles,
  ]);

  const renderDataSetState = () => {
    if (isLoading) {
      return <CircularProgress size={20} />;
    }
    if (failedToLoad || !reportEnabled) {
      return <Error color={'error'} />;
    }
    return <CheckCircle color={'success'} />;
  };

  return (
    <Box>
      <Button
        sx={sxStyles.selectorToggle}
        onClick={handleToggleSelectors}
        disabled={isLoading || !reportEnabled}
        startIcon={renderDataSetState()}
        endIcon={<ArrowDropDown />}
        className={'MuiOutlinedInput-input'}
      >
        {reportEnabled ? resolveCurrentSettings() : t('unavailable')}
      </Button>

      <Popper
        open={!!anchorEl}
        anchorEl={anchorEl}
        placement={'bottom-start'}
        sx={sxStyles.popper}
      >
        <Paper sx={sxStyles.popperPaper}>
          <ReportTypeSelector
            reportSelection={selectorSelection}
            reportConfig={reportConfig}
            onTypeChange={handleReportTypeChange}
            onRoleChange={handleRoleSelection}
            onUnitChange={handleUnitSelection}
            onLegalEntityChange={handleLegalEntitySelection}
            onIncludeSubUnits={handleIncludeSubunits}
          />
          {!!renderParamsFields && (
            <>
              <Divider orientation={'vertical'} sx={sxStyles.divider} />
              <Box sx={sxStyles.paramsFieldSet}>
                {renderParamsFields({
                  params:
                    selectorSelection.params ??
                    ({} as AnalyticsReportParams<P>),
                  onChange: handleParamsChange,
                  reportConfig,
                })}
              </Box>
            </>
          )}
          <Divider orientation={'vertical'} sx={sxStyles.divider} />
          <Box sx={sxStyles.actionsFieldSet}>
            <Button onClick={resetReportSettings}>{t('cancel')}</Button>
            <Button onClick={applyReportSettings}>{t('apply')}</Button>
          </Box>
        </Paper>
      </Popper>
    </Box>
  );
};
